import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemText,
  MenuItem,
  Paper,
  Snackbar,
  Alert as MuiAlert,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  OpenInFull as OpenInFullIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import {
  Wifi as WifiIcon,
  Bluetooth as BluetoothIcon,
  CellTower as CellTowerIcon,
  SatelliteAlt as GnssIcon,
  Warning as WarningIcon,
  FlightTakeoff as DroneIcon,
  TrackChanges as TrackerIcon,
  PlaylistAddCheck as WatchlistIcon,
} from '@mui/icons-material';
import { SignalEnvironmentData, WiFiObservation, BluetoothObservation, CellularObservation } from '../../types/signals';
import WiFiIEPanel from '../shared/WiFiIEPanel';
import { getIESummaryChips, decodeWiFiIE } from '../../utils/wifiIEDecoder';
import { dfpAdminService } from '../../services/dfpAdminService';

const DEVICE_TYPE_COLORS: Record<string, string> = {
  'Phone': '#26a69a',
  'Tablet': '#26a69a',
  'Laptop': '#5c6bc0',
  'Wearable': '#ab47bc',
  'Headphones': '#ab47bc',
  'Speaker': '#7e57c2',
  'TV / Display': '#5c6bc0',
  'Beacon': '#ff9800',
  'Tracker': '#e91e63',
  'Vehicle Hotspot': '#42a5f5',
  'Phone Hotspot': '#26a69a',
  'IoT Sensor': '#78909c',
  'Security Camera': '#ef5350',
  'Drone': '#f44336',
  'WiFi AP': '#66bb6a',
  'Unknown': '#9e9e9e',
};

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 250];
/** Compact right-panel preview — dig deeper via the floating table. */
const PREVIEW_ROWS = 8;
const DETAIL_HEADER_HEIGHT = 40;
const DETAIL_MIN_W = 640;
const DETAIL_MIN_H = 320;
const DETAIL_DEFAULT_W = 1100;
const DETAIL_DEFAULT_H = 480;
const DETAIL_MARGIN = 12;

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

export interface SignalLayerVisibility {
  wifi: boolean;
  bluetooth: boolean;
  cellular: boolean;
}

interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface SignalEnvironmentProps {
  data: SignalEnvironmentData | null;
  loading?: boolean;
  layerVisibility?: SignalLayerVisibility;
  onLayerVisibilityChange?: (v: SignalLayerVisibility) => void;
  mapBounds?: MapBounds | null;
  onSignalFocus?: (lat: number, lon: number) => void;
  onSignalUnfocus?: () => void;
  onSearchChange?: (term: string) => void;
  /** Controlled device-type filter (empty = all types). Syncs list + map. */
  deviceTypeFilter?: string[];
  onDeviceTypeFilterChange?: (types: string[]) => void;
}

type SortDir = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const av = a[orderBy], bv = b[orderBy];
  if (bv == null) return -1;
  if (av == null) return 1;
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}

function getComparator<T>(order: SortDir, orderBy: keyof T) {
  return order === 'desc'
    ? (a: T, b: T) => descendingComparator(a, b, orderBy)
    : (a: T, b: T) => -descendingComparator(a, b, orderBy);
}

const isInBounds = (lat: number | null, lon: number | null, bounds: MapBounds | null | undefined): boolean => {
  if (!bounds || lat == null || lon == null) return true;
  return lat >= bounds.south && lat <= bounds.north && lon >= bounds.west && lon <= bounds.east;
};

const SignalEnvironment: React.FC<SignalEnvironmentProps> = ({
  data,
  loading,
  layerVisibility,
  onLayerVisibilityChange,
  mapBounds,
  onSignalFocus,
  onSignalUnfocus,
  onSearchChange,
  deviceTypeFilter,
  onDeviceTypeFilterChange,
}) => {
  const [tab, setTab] = useState(0);
  const [wifiSort, setWifiSort] = useState<{ key: keyof WiFiObservation; dir: SortDir }>({ key: 'observation_count', dir: 'desc' });
  const [btSort, setBtSort] = useState<{ key: keyof BluetoothObservation; dir: SortDir }>({ key: 'observation_count', dir: 'desc' });
  const [cellSort, setCellSort] = useState<{ key: keyof CellularObservation; dir: SortDir }>({ key: 'observation_count', dir: 'desc' });
  const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);
  const [expandedWifiId, setExpandedWifiId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [internalTypeFilter, setInternalTypeFilter] = useState<string[]>([]);
  const typeFilter = deviceTypeFilter ?? internalTypeFilter;
  const setTypeFilter = (next: string[] | ((prev: string[]) => string[])) => {
    const resolved = typeof next === 'function' ? next(typeFilter) : next;
    if (onDeviceTypeFilterChange) onDeviceTypeFilterChange(resolved);
    else setInternalTypeFilter(resolved);
  };
  const [hideRandomized, setHideRandomized] = useState(false);
  const [wlSnack, setWlSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });
  const [wlSaving, setWlSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSize, setDetailSize] = useState({ width: DETAIL_DEFAULT_W, height: DETAIL_DEFAULT_H });
  const [detailPos, setDetailPos] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const h = typeof window !== 'undefined' ? window.innerHeight : 800;
    const width = Math.min(DETAIL_DEFAULT_W, w - 2 * DETAIL_MARGIN);
    return { left: DETAIL_MARGIN, top: Math.max(DETAIL_MARGIN, h - DETAIL_DEFAULT_H - 96), width };
  });
  const detailDragRef = useRef<{
    mode: 'move' | 'resize-corner';
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
    startWidth: number;
    startHeight: number;
  } | null>(null);

  const addToWatchlist = async (
    e: React.MouseEvent,
    identifier: string,
    signal_type: 'wifi' | 'bluetooth',
    label?: string,
  ) => {
    e.stopPropagation();
    if (!identifier || wlSaving) return;
    setWlSaving(true);
    try {
      await dfpAdminService.createWatchlist({
        identifier,
        identifier_type: 'mac',
        match_mode: 'exact',
        signal_type,
        severity_floor: 'critical',
        label: label || `Flagged from Signal Environment`,
      });
      setWlSnack({ open: true, msg: `Added ${identifier} to watchlist`, sev: 'success' });
    } catch (err: any) {
      setWlSnack({ open: true, msg: err?.message || 'Failed to add to watchlist', sev: 'error' });
    } finally {
      setWlSaving(false);
    }
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    onSearchChange?.(term);
  };

  const wifi = data?.wifi ?? [];
  const bt = data?.bluetooth ?? [];
  const cell = data?.cellular ?? [];
  const gnss = data?.gnss && 'total_observations' in data.gnss ? data.gnss : null;

  const search = searchTerm.toLowerCase().trim();

  const filteredWifi = useMemo(() => {
    let items = wifi.filter(w => {
      if (!isInBounds(w.latitude, w.longitude, mapBounds)) return false;
      if (!search) return true;
      return (w.bssid || '').toLowerCase().includes(search)
        || (w.ssid || '').toLowerCase().includes(search)
        || (w.manufacturer || '').toLowerCase().includes(search);
    });
    if (typeFilter.length > 0) items = items.filter(w => typeFilter.includes(w.device_type || 'Unknown'));
    if (hideRandomized) items = items.filter(w => !w.is_randomized);
    return items;
  }, [wifi, mapBounds, search, typeFilter, hideRandomized]);

  const filteredBt = useMemo(() => {
    let items = bt.filter(b => {
      if (!isInBounds(b.latitude, b.longitude, mapBounds)) return false;
      if (!search) return true;
      return (b.address || '').toLowerCase().includes(search)
        || (b.name || '').toLowerCase().includes(search)
        || (b.manufacturer || '').toLowerCase().includes(search);
    });
    if (typeFilter.length > 0) items = items.filter(b => typeFilter.includes(b.device_type || 'Unknown'));
    if (hideRandomized) items = items.filter(b => !b.is_randomized);
    return items;
  }, [bt, mapBounds, search, typeFilter, hideRandomized]);

  const filteredCell = useMemo(() => cell.filter(c => {
    if (!isInBounds(c.latitude, c.longitude, mapBounds)) return false;
    if (!search) return true;
    return String(c.cell_tower_id || '').toLowerCase().includes(search)
      || (c.operator || '').toLowerCase().includes(search)
      || (c.network_type || '').toLowerCase().includes(search);
  }), [cell, mapBounds, search]);

  const sortedWifi = useMemo(() => [...filteredWifi].sort(getComparator(wifiSort.dir, wifiSort.key)), [filteredWifi, wifiSort]);
  const sortedBt = useMemo(() => [...filteredBt].sort(getComparator(btSort.dir, btSort.key)), [filteredBt, btSort]);
  const sortedCell = useMemo(() => [...filteredCell].sort(getComparator(cellSort.dir, cellSort.key)), [filteredCell, cellSort]);

  // Reset page when the active tab's result set or filters change.
  useEffect(() => {
    setPage(0);
  }, [tab, search, typeFilter, hideRandomized, mapBounds, filteredWifi.length, filteredBt.length, filteredCell.length]);

  const activeCount =
    tab === 0 ? sortedWifi.length : tab === 1 ? sortedBt.length : tab === 2 ? sortedCell.length : 0;
  const safePage = Math.min(page, Math.max(0, Math.ceil(activeCount / rowsPerPage) - 1 || 0));
  const pagedWifi = useMemo(
    () => sortedWifi.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [sortedWifi, safePage, rowsPerPage],
  );
  const pagedBt = useMemo(
    () => sortedBt.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [sortedBt, safePage, rowsPerPage],
  );
  const pagedCell = useMemo(
    () => sortedCell.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [sortedCell, safePage, rowsPerPage],
  );

  const handleSignalClick = (id: string, lat: number | null, lon: number | null) => {
    if (selectedSignalId === id) {
      setSelectedSignalId(null);
      onSignalUnfocus?.();
    } else {
      setSelectedSignalId(id);
      if (lat != null && lon != null) onSignalFocus?.(lat, lon);
    }
  };

  const drones = useMemo(() => [...wifi.filter(w => w.is_drone), ...bt.filter(b => b.is_drone)], [wifi, bt]);
  const trackers = useMemo(() => [...wifi.filter(w => w.is_tracker), ...bt.filter(b => b.is_tracker)], [wifi, bt]);

  // Pre-compute the device-type breakdown for the collapsible "Breakdown"
  // accordion. We aggregate across WiFi + Bluetooth (cellular has its own
  // operator-keyed shape and lives in its own card below).
  // NOTE: these hooks must live above the early returns below — React
  // requires every hook to be called in the same order on every render.
  const typeCountMap = useMemo<Record<string, number>>(() => {
    const acc: Record<string, number> = {};
    for (const d of wifi as any[]) {
      const t = (d.device_type as string) || 'Unknown';
      acc[t] = (acc[t] || 0) + 1;
    }
    for (const d of bt as any[]) {
      const t = (d.device_type as string) || 'Unknown';
      acc[t] = (acc[t] || 0) + 1;
    }
    return acc;
  }, [wifi, bt]);
  const sortedTypes = useMemo<[string, number][]>(
    () => (Object.entries(typeCountMap) as [string, number][]).sort((a, b) => b[1] - a[1]),
    [typeCountMap]
  );
  const maxTypeCount = sortedTypes[0]?.[1] ?? 1;
  const topTypes = sortedTypes.slice(0, 6);

  // Drag/resize handlers for the floating detail panel. These are only used by
  // the main body below, but they must be declared here: the early returns that
  // follow would otherwise skip them and change the hook count between renders.
  const clampDetailIntoView = useCallback((left: number, top: number, width: number, height: number) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(width, vw - 2 * DETAIL_MARGIN);
    return {
      left: clamp(left, DETAIL_MARGIN, Math.max(DETAIL_MARGIN, vw - w - DETAIL_MARGIN)),
      top: clamp(top, DETAIL_MARGIN, Math.max(DETAIL_MARGIN, vh - height - DETAIL_MARGIN)),
    };
  }, []);

  const onDetailPointerMove = useCallback((e: PointerEvent) => {
    const d = detailDragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (d.mode === 'move') {
      const next = clampDetailIntoView(d.startLeft + dx, d.startTop + dy, d.startWidth, d.startHeight);
      setDetailPos((p) => ({ ...p, left: next.left, top: next.top }));
    } else {
      setDetailSize({
        width: clamp(d.startWidth + dx, DETAIL_MIN_W, window.innerWidth - d.startLeft - DETAIL_MARGIN),
        height: clamp(d.startHeight + dy, DETAIL_MIN_H, window.innerHeight - d.startTop - DETAIL_MARGIN),
      });
    }
  }, [clampDetailIntoView]);

  const endDetailDrag = useCallback(() => {
    detailDragRef.current = null;
    window.removeEventListener('pointermove', onDetailPointerMove);
    window.removeEventListener('pointerup', endDetailDrag);
    document.body.style.userSelect = '';
  }, [onDetailPointerMove]);

  const beginDetailDrag = useCallback(
    (mode: 'move' | 'resize-corner') => (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      detailDragRef.current = {
        mode,
        startX: e.clientX,
        startY: e.clientY,
        startLeft: detailPos.left,
        startTop: detailPos.top,
        startWidth: detailSize.width,
        startHeight: detailSize.height,
      };
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onDetailPointerMove);
      window.addEventListener('pointerup', endDetailDrag);
    },
    [detailPos, detailSize, onDetailPointerMove, endDetailDrag],
  );

  useEffect(() => endDetailDrag, [endDetailDrag]);

  // Show the empty state only when we genuinely have nothing — either the
  // parent hasn't supplied a `data` object yet, or it supplied one whose
  // wifi/bluetooth/cellular arrays are all empty (e.g. server returned no
  // observations for this date range). This avoids a stale empty state when
  // the parent's signalData reference flips before child props propagate.
  const hasAnyObservations = !!data && (
    (data.wifi?.length ?? 0) > 0 ||
    (data.bluetooth?.length ?? 0) > 0 ||
    (data.cellular?.length ?? 0) > 0 ||
    !!data.gnss
  );
  if (!hasAnyObservations && !loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Use LOAD &rarr; Load Signal Environment to view collected RF data.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 1 }} color="text.secondary">Loading signal data...</Typography>
      </Box>
    );
  }

  const handleWifiSort = (key: keyof WiFiObservation) => {
    setWifiSort(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  };
  const handleBtSort = (key: keyof BluetoothObservation) => {
    setBtSort(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  };
  const handleCellSort = (key: keyof CellularObservation) => {
    setCellSort(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const hasRandomized = (data?.wifi?.some(w => w.is_randomized) || data?.bluetooth?.some(b => b.is_randomized)) ?? false;
  const filtersActive = !!searchTerm || typeFilter.length > 0 || hideRandomized;

  // 5-tile stat cards. Clicking a card toggles the matching map layer when
  // applicable (WiFi/BLE/Cell), matching the previous chip behavior. GNSS and
  // the Drones/Trackers card are display-only.
  const statTiles: Array<{
    key: string;
    label: string;
    value: string;
    icon: React.ReactNode;
    color: string;
    onClick?: () => void;
    active?: boolean;
  }> = [
    {
      key: 'wifi',
      label: 'WiFi',
      value: wifi.length.toLocaleString(),
      icon: <WifiIcon sx={{ fontSize: 12 }} />,
      color: '#2196f3',
      active: layerVisibility?.wifi !== false,
      onClick: () => onLayerVisibilityChange?.({ ...(layerVisibility ?? { wifi: true, bluetooth: true, cellular: true }), wifi: !layerVisibility?.wifi }),
    },
    {
      key: 'bt',
      label: 'BLE',
      value: bt.length.toLocaleString(),
      icon: <BluetoothIcon sx={{ fontSize: 12 }} />,
      color: '#00bcd4',
      active: layerVisibility?.bluetooth !== false,
      onClick: () => onLayerVisibilityChange?.({ ...(layerVisibility ?? { wifi: true, bluetooth: true, cellular: true }), bluetooth: !layerVisibility?.bluetooth }),
    },
    {
      key: 'cell',
      label: 'Cell',
      value: cell.length.toLocaleString(),
      icon: <CellTowerIcon sx={{ fontSize: 12 }} />,
      color: '#ff9800',
      active: layerVisibility?.cellular !== false,
      onClick: () => onLayerVisibilityChange?.({ ...(layerVisibility ?? { wifi: true, bluetooth: true, cellular: true }), cellular: !layerVisibility?.cellular }),
    },
    {
      key: 'gnss',
      label: 'GNSS',
      value: (gnss?.total_observations ?? 0).toLocaleString(),
      icon: <GnssIcon sx={{ fontSize: 12 }} />,
      color: '#4caf50',
    },
    {
      key: 'flagged',
      label: drones.length > 0 ? 'Drones' : 'Trackers',
      value: (drones.length || trackers.length).toLocaleString(),
      icon: drones.length > 0 ? <DroneIcon sx={{ fontSize: 12 }} /> : <TrackerIcon sx={{ fontSize: 12 }} />,
      color: '#f44336',
    },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
      {/* Compact 5-tile stat row (WIFI / BLE / CELL / GNSS / FLAGGED).
          Clickable WiFi/BLE/Cell tiles toggle the matching map layer. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, mb: 1 }}>
        {statTiles.map(({ key, label, value, icon, color, onClick, active }) => {
          const isInteractive = !!onClick;
          const isDimmed = isInteractive && active === false;
          return (
            <Box
              key={key}
              onClick={onClick}
              sx={{
                py: 0.5,
                px: 0.5,
                borderRadius: 0.5,
                bgcolor: isDimmed ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)',
                border: '1px solid',
                borderColor: isInteractive && active ? color : 'divider',
                textAlign: 'center',
                cursor: isInteractive ? 'pointer' : 'default',
                opacity: isDimmed ? 0.55 : 1,
                transition: 'border-color 120ms, background-color 120ms, opacity 120ms',
                '&:hover': isInteractive ? { bgcolor: 'rgba(255,255,255,0.06)' } : undefined,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, color }}>
                {icon}
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.5rem', textTransform: 'uppercase', letterSpacing: 0.3, lineHeight: 1, color }}
                >
                  {label}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                {value}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Breakdown — top device types as horizontal bars. Mirrors the
          Dwell Analysis "Breakdown" card so analysts read both the same way. */}
      {topTypes.length > 0 && (
        <Accordion
          defaultExpanded={false}
          sx={{
            mb: 0.75,
            '&::before': { display: 'none' },
            boxShadow: 'none',
            bgcolor: 'transparent',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '4px !important',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
            sx={{ minHeight: 28, '&.Mui-expanded': { minHeight: 28 }, py: 0, '& .MuiAccordionSummary-content': { my: 0.25 } }}
          >
            <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
              Breakdown
            </Typography>
            <Typography variant="caption" sx={{ ml: 1, fontSize: '0.65rem', color: 'text.secondary' }}>
              top device types
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 0.75, px: 1.5 }}>
            {topTypes.map(([type, count]) => {
              const color = DEVICE_TYPE_COLORS[type] || '#9e9e9e';
              return (
                <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ width: 90, flexShrink: 0, fontSize: '0.6rem' }} noWrap>
                    {type}
                  </Typography>
                  <Box sx={{ flex: 1, height: 6, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                    <Box sx={{ width: `${(count / maxTypeCount) * 100}%`, height: '100%', bgcolor: color, borderRadius: 1 }} />
                  </Box>
                  <Typography variant="caption" sx={{ width: 36, textAlign: 'right', fontSize: '0.6rem', fontWeight: 600 }}>
                    {count}
                  </Typography>
                </Box>
              );
            })}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Filters — collapsed by default; shows an "Active" chip when any
          filter is non-default. Same UX as the Dwell Analysis Filters card. */}
      <Accordion
        defaultExpanded={false}
        sx={{
          mb: 0.75,
          '&::before': { display: 'none' },
          boxShadow: 'none',
          bgcolor: 'transparent',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '4px !important',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
          sx={{ minHeight: 28, '&.Mui-expanded': { minHeight: 28 }, py: 0, '& .MuiAccordionSummary-content': { my: 0.25 } }}
        >
          <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
            Filters
          </Typography>
          {filtersActive && (
            <Chip
              label="Active"
              size="small"
              color="primary"
              sx={{ ml: 1, height: 14, fontSize: '0.5rem', '& .MuiChip-label': { px: 0.4 } }}
            />
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 0.75, px: 1.5 }}>
          <Stack spacing={1}>
            <TextField
              size="small"
              placeholder="Search signals by name, ID, manufacturer..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                sx: { fontSize: '0.75rem', height: 30 },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {sortedTypes.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 170, flex: 1 }}>
                  <Select
                    multiple
                    displayEmpty
                    value={typeFilter}
                    onChange={(e) => {
                      const val = e.target.value;
                      setTypeFilter(typeof val === 'string' ? val.split(',') : (val as string[]));
                    }}
                    renderValue={(selected) => {
                      if ((selected as string[]).length === 0) {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FilterIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>All Types</Typography>
                          </Box>
                        );
                      }
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Badge badgeContent={(selected as string[]).length} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14 } }}>
                            <FilterIcon sx={{ fontSize: 14 }} />
                          </Badge>
                          <Typography sx={{ fontSize: '0.7rem' }} noWrap>
                            {(selected as string[]).length === 1 ? (selected as string[])[0] : `${(selected as string[]).length} types`}
                          </Typography>
                        </Box>
                      );
                    }}
                    sx={{ height: 30, fontSize: '0.7rem', '& .MuiSelect-select': { py: 0.5 } }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                  >
                    {sortedTypes.map(([type, count]) => (
                      <MenuItem key={type} value={type} dense sx={{ py: 0.25 }}>
                        <Checkbox checked={typeFilter.includes(type)} size="small" sx={{ p: 0.25, mr: 0.5 }} />
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: DEVICE_TYPE_COLORS[type] || '#9e9e9e', mr: 1, flexShrink: 0 }} />
                        <ListItemText primary={type} sx={{ '& .MuiListItemText-primary': { fontSize: '0.75rem' } }} />
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 1 }}>{count as React.ReactNode}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {hasRandomized && (
                <Tooltip title="Randomized MAC addresses (Apple/Android privacy) hop every few minutes — hide them to focus on stable identifiers.">
                  <Box
                    onClick={() => setHideRandomized(!hideRandomized)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      px: 0.75, py: 0.25, borderRadius: 0.5, cursor: 'pointer',
                      border: '1px solid', borderColor: hideRandomized ? '#ff5722' : 'divider',
                      bgcolor: hideRandomized ? 'rgba(255,87,34,0.12)' : 'transparent',
                    }}
                  >
                    <Switch
                      checked={hideRandomized}
                      onChange={(e) => { e.stopPropagation(); setHideRandomized(e.target.checked); }}
                      size="small"
                      sx={{ p: 0, '& .MuiSwitch-thumb': { width: 10, height: 10 } }}
                    />
                    <Typography sx={{ fontSize: '0.65rem', color: hideRandomized ? '#ff5722' : 'text.secondary' }}>
                      Hide randomized
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Compact preview — enough context in the narrow tool panel; full
          columns live in the floating dig-deeper table (AdTech-style). */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75, flexWrap: 'wrap' }}>
        {([
          { i: 0, label: 'WiFi', n: filteredWifi.length },
          { i: 1, label: 'BLE', n: filteredBt.length },
          { i: 2, label: 'Cell', n: filteredCell.length },
          { i: 3, label: 'GNSS', n: gnss?.total_observations ?? 0 },
        ] as const).map(({ i, label, n }) => (
          <Chip
            key={label}
            size="small"
            label={`${label} ${typeof n === 'number' ? n.toLocaleString() : n}`}
            onClick={() => setTab(i)}
            color={tab === i ? 'primary' : 'default'}
            variant={tab === i ? 'filled' : 'outlined'}
            sx={{ height: 22, fontSize: '0.65rem', fontWeight: tab === i ? 700 : 500 }}
          />
        ))}
      </Box>

      <Button
        fullWidth
        size="small"
        variant="contained"
        startIcon={<OpenInFullIcon sx={{ fontSize: 16 }} />}
        onClick={() => setDetailOpen(true)}
        sx={{ mb: 1, textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
      >
        Open signal table
      </Button>

      {tab === 0 && (
        <Stack spacing={0.25} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mb: 1 }}>
          {sortedWifi.slice(0, PREVIEW_ROWS).map((w) => (
            <Box
              key={w.bssid}
              onClick={() => handleSignalClick(w.bssid, w.latitude, w.longitude)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 0.75,
                py: 0.5,
                borderRadius: 0.5,
                cursor: 'pointer',
                bgcolor: selectedSignalId === w.bssid ? 'action.selected' : 'rgba(255,255,255,0.03)',
                border: '1px solid',
                borderColor: selectedSignalId === w.bssid ? 'primary.main' : 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 600, fontSize: '0.7rem' }}>
                  {w.ssid || 'hidden SSID'}
                </Typography>
                <Typography variant="caption" noWrap sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.6rem', color: 'text.secondary' }}>
                  {w.bssid}
                </Typography>
              </Box>
              <Chip
                label={w.device_type || 'Unknown'}
                size="small"
                sx={{ bgcolor: DEVICE_TYPE_COLORS[w.device_type || 'Unknown'], color: '#fff', fontSize: '0.55rem', height: 18, maxWidth: 72 }}
              />
              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, minWidth: 36, textAlign: 'right' }}>
                {w.avg_signal_dbm != null ? `${w.avg_signal_dbm}` : '—'}
              </Typography>
            </Box>
          ))}
          {sortedWifi.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>No WiFi in view</Typography>
          )}
          {sortedWifi.length > PREVIEW_ROWS && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, pt: 0.5 }}>
              +{sortedWifi.length - PREVIEW_ROWS} more — open table to browse
            </Typography>
          )}
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={0.25} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mb: 1 }}>
          {sortedBt.slice(0, PREVIEW_ROWS).map((b) => (
            <Box
              key={b.address}
              onClick={() => handleSignalClick(b.address, b.latitude, b.longitude)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 0.75,
                py: 0.5,
                borderRadius: 0.5,
                cursor: b.latitude ? 'pointer' : 'default',
                bgcolor: selectedSignalId === b.address ? 'action.selected' : 'rgba(255,255,255,0.03)',
                border: '1px solid',
                borderColor: selectedSignalId === b.address ? 'primary.main' : 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 600, fontSize: '0.7rem' }}>
                  {b.name || 'unnamed'}
                </Typography>
                <Typography variant="caption" noWrap sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.6rem', color: 'text.secondary' }}>
                  {b.address}
                </Typography>
              </Box>
              <Chip
                label={b.device_type || 'Unknown'}
                size="small"
                sx={{ bgcolor: DEVICE_TYPE_COLORS[b.device_type || 'Unknown'], color: '#fff', fontSize: '0.55rem', height: 18, maxWidth: 72 }}
              />
              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, minWidth: 36, textAlign: 'right' }}>
                {b.avg_rssi != null ? `${b.avg_rssi}` : '—'}
              </Typography>
            </Box>
          ))}
          {sortedBt.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>No Bluetooth in view</Typography>
          )}
          {sortedBt.length > PREVIEW_ROWS && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, pt: 0.5 }}>
              +{sortedBt.length - PREVIEW_ROWS} more — open table to browse
            </Typography>
          )}
        </Stack>
      )}

      {tab === 2 && (
        <Stack spacing={0.25} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mb: 1 }}>
          {sortedCell.slice(0, PREVIEW_ROWS).map((c) => (
            <Box
              key={c.cell_tower_id}
              onClick={() => handleSignalClick(String(c.cell_tower_id), c.latitude, c.longitude)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 0.75,
                py: 0.5,
                borderRadius: 0.5,
                cursor: c.latitude ? 'pointer' : 'default',
                bgcolor: selectedSignalId === String(c.cell_tower_id) ? 'action.selected' : 'rgba(255,255,255,0.03)',
                border: '1px solid',
                borderColor: selectedSignalId === String(c.cell_tower_id) ? 'primary.main' : 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 600, fontSize: '0.7rem' }}>
                  {c.operator || `Cell ${c.cell_tower_id}`}
                </Typography>
                <Typography variant="caption" noWrap sx={{ display: 'block', fontSize: '0.6rem', color: 'text.secondary' }}>
                  {c.network_type || '—'} · ID {c.cell_tower_id}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, minWidth: 36, textAlign: 'right' }}>
                {c.avg_signal_dbm != null ? `${c.avg_signal_dbm}` : '—'}
              </Typography>
            </Box>
          ))}
          {sortedCell.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>No cellular in view</Typography>
          )}
          {sortedCell.length > PREVIEW_ROWS && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, pt: 0.5 }}>
              +{sortedCell.length - PREVIEW_ROWS} more — open table to browse
            </Typography>
          )}
        </Stack>
      )}

      {tab === 3 && (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mb: 1, px: 0.5 }}>
          {gnss ? (
            <Stack spacing={0.75}>
              <Typography variant="caption" color="text.secondary">
                {gnss.total_observations.toLocaleString()} obs · {gnss.unique_svs} SVs · fix {gnss.fix_rate}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg C/N0 {gnss.avg_cn0_dbhz ?? '—'} dB-Hz · in view/used {gnss.avg_sats_in_view ?? '—'}/{gnss.avg_sats_used ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Open table for constellation breakdown
              </Typography>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">No GNSS data</Typography>
          )}
        </Box>
      )}

      {detailOpen && createPortal(
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            left: detailPos.left,
            top: detailPos.top,
            width: Math.min(detailSize.width, (typeof window !== 'undefined' ? window.innerWidth : detailSize.width) - 2 * DETAIL_MARGIN),
            height: detailSize.height,
            zIndex: 1250,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(6px)',
          }}
        >
          <Box
            onPointerDown={beginDetailDrag('move')}
            sx={{
              height: DETAIL_HEADER_HEIGHT,
              minHeight: DETAIL_HEADER_HEIGHT,
              px: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'move',
              background: 'linear-gradient(90deg, rgba(33,150,243,0.18), rgba(33,150,243,0.04))',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <WifiIcon sx={{ fontSize: 18, color: '#2196f3' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
              Signal Environment
            </Typography>
            <Chip
              size="small"
              label={`${filteredWifi.length + filteredBt.length + filteredCell.length} signals`}
              sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(33,150,243,0.15)', color: '#2196f3', fontWeight: 700 }}
            />
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Close table">
              <IconButton
                size="small"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); setDetailOpen(false); }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 1 }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 36, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`WiFi (${filteredWifi.length}${filteredWifi.length !== wifi.length ? `/${wifi.length}` : ''})`} sx={{ minHeight: 36, py: 0 }} />
        <Tab label={`Bluetooth (${filteredBt.length}${filteredBt.length !== bt.length ? `/${bt.length}` : ''})`} sx={{ minHeight: 36, py: 0 }} />
        <Tab label={`Cellular (${filteredCell.length}${filteredCell.length !== cell.length ? `/${cell.length}` : ''})`} sx={{ minHeight: 36, py: 0 }} />
        <Tab label="GNSS Health" sx={{ minHeight: 36, py: 0 }} />
      </Tabs>

      {/* WiFi Tab */}
      {tab === 0 && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
          }}
        >
          <Table size="small" stickyHeader sx={{ minWidth: 'max-content' }}>
            <TableHead>
              <TableRow>
                <TableCell>BSSID</TableCell>
                <TableCell>SSID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell sortDirection={wifiSort.key === 'avg_signal_dbm' ? wifiSort.dir : false}>
                  <TableSortLabel active={wifiSort.key === 'avg_signal_dbm'} direction={wifiSort.dir} onClick={() => handleWifiSort('avg_signal_dbm')}>
                    Signal
                  </TableSortLabel>
                </TableCell>
                <TableCell>Freq / Ch</TableCell>
                <TableCell>Security</TableCell>
                <TableCell sortDirection={wifiSort.key === 'observation_count' ? wifiSort.dir : false}>
                  <TableSortLabel active={wifiSort.key === 'observation_count'} direction={wifiSort.dir} onClick={() => handleWifiSort('observation_count')}>
                    Count
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={wifiSort.key === 'distinct_locations' ? wifiSort.dir : false}>
                  <TableSortLabel active={wifiSort.key === 'distinct_locations'} direction={wifiSort.dir} onClick={() => handleWifiSort('distinct_locations')}>
                    Locs
                  </TableSortLabel>
                </TableCell>
                <TableCell>First Seen</TableCell>
                <TableCell>Last Seen</TableCell>
                <TableCell>Flags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedWifi.map((w) => (
                <React.Fragment key={w.bssid}>
                  <TableRow
                    hover
                    onClick={() => {
                      handleSignalClick(w.bssid, w.latitude, w.longitude);
                      if (w.information_elements) {
                        setExpandedWifiId(prev => prev === w.bssid ? null : w.bssid);
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      ...(selectedSignalId === w.bssid ? { bgcolor: 'action.selected' } : {}),
                      ...(w.is_drone ? { bgcolor: 'error.dark', '&:hover': { bgcolor: 'error.main' } } : w.is_tracker ? { bgcolor: 'warning.dark' } : {}),
                    }}
                  >
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{w.bssid}</TableCell>
                    <Tooltip title={w.ssid || ''} enterDelay={300} disableHoverListener={!w.ssid}>
                      <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {w.ssid || <Typography variant="caption" color="text.disabled">hidden</Typography>}
                        {w.badges?.map((b: string) => (
                          <Chip key={b} label={b} size="small" variant="outlined" sx={{ ml: 0.5, fontSize: '0.6rem', height: 16 }} />
                        ))}
                        {w.information_elements && (() => {
                          const decoded = decodeWiFiIE(w.information_elements!);
                          const chips = getIESummaryChips(decoded);
                          return chips.slice(0, 3).map((c, i) => (
                            <Chip key={i} label={c.label} color={c.color} size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 16 }} />
                          ));
                        })()}
                      </TableCell>
                    </Tooltip>
                    <TableCell sx={{ py: 0.5 }}>
                      <Chip label={w.device_type || 'Unknown'} size="small"
                        sx={{ bgcolor: DEVICE_TYPE_COLORS[w.device_type || 'Unknown'], color: '#fff', fontSize: '0.65rem', height: 20 }} />
                    </TableCell>
                    <Tooltip title={w.manufacturer || ''} enterDelay={300} disableHoverListener={!w.manufacturer}>
                      <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                        {w.manufacturer || '—'}
                      </TableCell>
                    </Tooltip>
                    <TableCell>{w.avg_signal_dbm != null ? `${w.avg_signal_dbm} dBm` : '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {w.frequency_mhz ? `${w.frequency_mhz} MHz` : '—'}{w.channel ? ` / Ch ${w.channel}` : ''}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{w.security || '—'}</TableCell>
                    <TableCell>{w.observation_count}</TableCell>
                    <TableCell>{w.distinct_locations}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>{formatTime(w.first_seen)}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>{formatTime(w.last_seen)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {w.is_drone && <Tooltip title="Drone detected"><DroneIcon fontSize="small" color="error" /></Tooltip>}
                        {w.is_tracker && <Tooltip title="Known tracker"><TrackerIcon fontSize="small" color="warning" /></Tooltip>}
                        {w.is_la_bit && <Tooltip title="Locally-administered MAC"><WarningIcon fontSize="small" sx={{ color: 'text.disabled', fontSize: 16 }} /></Tooltip>}
                        <Tooltip title="Add to threat watchlist">
                          <IconButton size="small" disabled={wlSaving}
                            onClick={(e) => addToWatchlist(e, w.bssid, 'wifi', w.ssid || w.manufacturer || undefined)}
                            sx={{ p: 0.25, color: '#f44336' }}>
                            <WatchlistIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {expandedWifiId === w.bssid && w.information_elements && (
                    <TableRow>
                      <TableCell colSpan={12} sx={{ p: 0, border: 'none' }}>
                        <Collapse in={true}>
                          <Box sx={{ p: 1.5 }}>
                            <WiFiIEPanel informationElements={w.information_elements} isDarkMode={true} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
              {filteredWifi.length === 0 && (
                <TableRow><TableCell colSpan={12} align="center"><Typography color="text.secondary">No WiFi data</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {sortedWifi.length > 0 && (
          <Box sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
            <TablePagination
              component="div"
              count={sortedWifi.length}
              page={safePage}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Per page:"
              sx={{
                minHeight: 40,
                '& .MuiTablePagination-toolbar': { minHeight: 40, pl: 1, pr: 1 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.65rem' },
                '& .MuiTablePagination-select': { fontSize: '0.7rem' },
              }}
            />
          </Box>
        )}
        </Box>
      )}

      {/* Bluetooth Tab */}
      {tab === 1 && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
          }}
        >
          <Table size="small" stickyHeader sx={{ minWidth: 'max-content' }}>
            <TableHead>
              <TableRow>
                <TableCell>Address</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Device Type</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell sortDirection={btSort.key === 'avg_rssi' ? btSort.dir : false}>
                  <TableSortLabel active={btSort.key === 'avg_rssi'} direction={btSort.dir} onClick={() => handleBtSort('avg_rssi')}>
                    RSSI
                  </TableSortLabel>
                </TableCell>
                <TableCell>BT Type</TableCell>
                <TableCell>Dist</TableCell>
                <TableCell sortDirection={btSort.key === 'observation_count' ? btSort.dir : false}>
                  <TableSortLabel active={btSort.key === 'observation_count'} direction={btSort.dir} onClick={() => handleBtSort('observation_count')}>
                    Count
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={btSort.key === 'distinct_locations' ? btSort.dir : false}>
                  <TableSortLabel active={btSort.key === 'distinct_locations'} direction={btSort.dir} onClick={() => handleBtSort('distinct_locations')}>
                    Locs
                  </TableSortLabel>
                </TableCell>
                <TableCell>First Seen</TableCell>
                <TableCell>Last Seen</TableCell>
                <TableCell>Flags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedBt.map((b) => (
                <TableRow
                  key={b.address}
                  hover
                  onClick={() => handleSignalClick(b.address, b.latitude, b.longitude)}
                  sx={{
                    cursor: b.latitude ? 'pointer' : 'default',
                    ...(selectedSignalId === b.address ? { bgcolor: 'action.selected' } : {}),
                    ...(b.is_drone ? { bgcolor: 'error.dark' } : b.is_tracker ? { bgcolor: 'warning.dark' } : {}),
                  }}
                >
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{b.address}</TableCell>
                  <Tooltip title={b.name || ''} enterDelay={300} disableHoverListener={!b.name}>
                    <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.name || <Typography variant="caption" color="text.disabled">unnamed</Typography>}
                      {b.badges?.map((bg: string) => (
                        <Chip key={bg} label={bg} size="small" variant="outlined" sx={{ ml: 0.5, fontSize: '0.6rem', height: 16 }} />
                      ))}
                    </TableCell>
                  </Tooltip>
                  <TableCell sx={{ py: 0.5 }}>
                    <Chip label={b.device_type || 'Unknown'} size="small"
                      sx={{ bgcolor: DEVICE_TYPE_COLORS[b.device_type || 'Unknown'], color: '#fff', fontSize: '0.65rem', height: 20 }} />
                  </TableCell>
                  <Tooltip title={b.manufacturer || ''} enterDelay={300} disableHoverListener={!b.manufacturer}>
                    <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      {b.manufacturer || '—'}
                    </TableCell>
                  </Tooltip>
                  <TableCell>{b.avg_rssi != null ? `${b.avg_rssi} dBm` : '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{b.bt_type || '—'}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {b.estimated_distance_m ? `~${b.estimated_distance_m}m` : '—'}
                  </TableCell>
                  <TableCell>{b.observation_count}</TableCell>
                  <TableCell>{b.distinct_locations}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem' }}>{formatTime(b.first_seen)}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem' }}>{formatTime(b.last_seen)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {b.is_drone && <Tooltip title="Drone detected"><DroneIcon fontSize="small" color="error" /></Tooltip>}
                      {b.is_tracker && <Tooltip title="Known tracker"><TrackerIcon fontSize="small" color="warning" /></Tooltip>}
                      {b.is_la_bit && <Tooltip title="Randomized MAC"><WarningIcon fontSize="small" sx={{ color: 'text.disabled', fontSize: 16 }} /></Tooltip>}
                      <Tooltip title="Add to threat watchlist">
                        <IconButton size="small" disabled={wlSaving}
                          onClick={(e) => addToWatchlist(e, b.address, 'bluetooth', b.name || b.manufacturer || undefined)}
                          sx={{ p: 0.25, color: '#f44336' }}>
                          <WatchlistIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {filteredBt.length === 0 && (
                <TableRow><TableCell colSpan={13} align="center"><Typography color="text.secondary">No Bluetooth data</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {sortedBt.length > 0 && (
          <Box sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
            <TablePagination
              component="div"
              count={sortedBt.length}
              page={safePage}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Per page:"
              sx={{
                minHeight: 40,
                '& .MuiTablePagination-toolbar': { minHeight: 40, pl: 1, pr: 1 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.65rem' },
                '& .MuiTablePagination-select': { fontSize: '0.7rem' },
              }}
            />
          </Box>
        )}
        </Box>
      )}

      {/* Cellular Tab */}
      {tab === 2 && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
          }}
        >
          <Table size="small" stickyHeader sx={{ minWidth: 'max-content' }}>
            <TableHead>
              <TableRow>
                <TableCell>Cell ID</TableCell>
                <TableCell>Operator</TableCell>
                <TableCell>MCC/MNC</TableCell>
                <TableCell>Network</TableCell>
                <TableCell>PCI</TableCell>
                <TableCell>ARFCN</TableCell>
                <TableCell sortDirection={cellSort.key === 'avg_signal_dbm' ? cellSort.dir : false}>
                  <TableSortLabel active={cellSort.key === 'avg_signal_dbm'} direction={cellSort.dir} onClick={() => handleCellSort('avg_signal_dbm')}>
                    Signal
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={cellSort.key === 'observation_count' ? cellSort.dir : false}>
                  <TableSortLabel active={cellSort.key === 'observation_count'} direction={cellSort.dir} onClick={() => handleCellSort('observation_count')}>
                    Count
                  </TableSortLabel>
                </TableCell>
                <TableCell>First Seen</TableCell>
                <TableCell>Last Seen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedCell.map((c) => (
                <TableRow
                  key={c.cell_tower_id}
                  hover
                  onClick={() => handleSignalClick(String(c.cell_tower_id), c.latitude, c.longitude)}
                  sx={{
                    cursor: c.latitude ? 'pointer' : 'default',
                    ...(selectedSignalId === String(c.cell_tower_id) ? { bgcolor: 'action.selected' } : {}),
                  }}
                >
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{c.cell_tower_id}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{c.operator || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{c.mcc && c.mnc ? `${c.mcc}/${c.mnc}` : '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{c.network_type || '—'}</TableCell>
                  <TableCell>{c.pci || '—'}</TableCell>
                  <TableCell>{c.nrarfcn || '—'}</TableCell>
                  <TableCell>{c.avg_signal_dbm != null ? `${c.avg_signal_dbm} dBm` : '—'}</TableCell>
                  <TableCell>{c.observation_count}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem' }}>{formatTime(c.first_seen)}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem' }}>{formatTime(c.last_seen)}</TableCell>
                </TableRow>
              ))}
              {cell.length === 0 && (
                <TableRow><TableCell colSpan={10} align="center"><Typography color="text.secondary">No cellular data</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {sortedCell.length > 0 && (
          <Box sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
            <TablePagination
              component="div"
              count={sortedCell.length}
              page={safePage}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Per page:"
              sx={{
                minHeight: 40,
                '& .MuiTablePagination-toolbar': { minHeight: 40, pl: 1, pr: 1 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.65rem' },
                '& .MuiTablePagination-select': { fontSize: '0.7rem' },
              }}
            />
          </Box>
        )}
        </Box>
      )}

      {/* GNSS Health Tab */}
      {tab === 3 && (
        <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
          {gnss ? (
            <>
              <Stack direction="row" spacing={3} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Observations</Typography>
                  <Typography variant="h5">{gnss.total_observations.toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Unique SVs</Typography>
                  <Typography variant="h5">{gnss.unique_svs}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Avg C/N0</Typography>
                  <Typography variant="h5">{gnss.avg_cn0_dbhz ?? '—'} <Typography component="span" variant="caption">dB-Hz</Typography></Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Fix Rate</Typography>
                  <Typography variant="h5" color={gnss.fix_rate > 70 ? 'success.main' : gnss.fix_rate > 40 ? 'warning.main' : 'error.main'}>
                    {gnss.fix_rate}%
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Avg SVs In View / Used</Typography>
                  <Typography variant="h5">{gnss.avg_sats_in_view ?? '—'} / {gnss.avg_sats_used ?? '—'}</Typography>
                </Box>
              </Stack>

              {gnss.constellations && gnss.constellations.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Per-Constellation Breakdown</Typography>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ maxWidth: '100%', overflowX: 'auto', minWidth: 0 }}
                  >
                    <Table size="small" sx={{ minWidth: 'max-content' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Constellation</TableCell>
                          <TableCell>SVs Tracked</TableCell>
                          <TableCell>Observations</TableCell>
                          <TableCell>Avg C/N0</TableCell>
                          <TableCell>Used in Fix</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {gnss.constellations.map((c) => (
                          <TableRow key={c.name} hover>
                            <TableCell>{c.name}</TableCell>
                            <TableCell>{c.sv_count}</TableCell>
                            <TableCell>{c.observations.toLocaleString()}</TableCell>
                            <TableCell>{c.avg_cn0 ?? '—'} dB-Hz</TableCell>
                            <TableCell>{c.used_count.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Data from {formatTime(gnss.first_seen)} to {formatTime(gnss.last_seen)}
              </Typography>
            </>
          ) : (
            <Typography color="text.secondary">No GNSS data available.</Typography>
          )}
        </Box>
      )}
          </Box>

          {/* Resize handle (bottom-right), mirrors AdTech results table. */}
          <Box
            onPointerDown={beginDetailDrag('resize-corner')}
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: 18,
              height: 18,
              cursor: 'nwse-resize',
              background: 'linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.18) 50%)',
            }}
          />
        </Paper>,
        document.body,
      )}

      <Snackbar
        open={wlSnack.open}
        autoHideDuration={4000}
        onClose={() => setWlSnack(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert severity={wlSnack.sev} variant="filled" onClose={() => setWlSnack(prev => ({ ...prev, open: false }))}>
          {wlSnack.msg}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default SignalEnvironment;
